import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { Bell, X } from 'lucide-react';

import './index.css';

// Pages
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import { AuthForm } from './components/AuthForm';
import GameDetailsPage from './pages/GameDetailsPage';
import AddGamePage from './pages/AddGamePage';
import EditGamePage from './pages/EditGamePage';
import CollectionPage from './pages/CollectionPage';
import PlayedGamesPage from './features/played-games/PlayedGamesPage';
import WishlistPage from './pages/WishlistPage';
import AdminPage from './pages/AdminPage';
import OnboardingPage, { useOnboardingStatus } from './pages/OnboardingPage';
import RecommendationsPage from './pages/RecommendationsPage';
import ErrorPage from './pages/ErrorPage';
import NotFoundPage from './pages/NotFoundPage';
import CollectionStatsPage from './pages/CollectionStatsPage';
import RankingPage from './pages/RankingPage';
import PublicProfilePage from './components/PublicProfilePage';
import UserSearchPage from './components/UserSearchPage';
import MarketPage from './pages/MarketPage';
import MarketListingDetails from './pages/MarketListingDetails';
import LudopediaCallback from './components/LudopediaCallback';
import GameReviewsPage from './pages/GameReviewsPage';
import FeedbackPage from './pages/FeedbackPage';
import UserConnectionsPage from './components/UserConnectionsPage';
import { MessagesPage, ConversationPage } from './pages/messages';
import { ProfileForm } from './components/ProfileForm';
import FeedPage from './pages/FeedPage';
import PostPage from './pages/PostPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import AccountDeletionPage from './pages/AccountDeletionPage';
import DeleteAccountRequestPage from './pages/DeleteAccountRequestPage';
import SessionDetailsPage from './pages/SessionDetailsPage';
import StoresPage from './pages/StoresPage';
import StoreProductsPage from './pages/StoreProductsPage';
import GamePricesPage from './pages/GamePricesPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import UserProfilePage from './components/UserProfilePage';
import XpHistoryPage from './pages/XpHistoryPage';
import MyBadgesPage from './pages/MyBadgesPage';
import UserMissionsPage from './pages/UserMissionsPage';
import { CompleteProfilePage } from './pages/CompleteProfilePage';
import ChooseMascotPage from './pages/ChooseMascotPage';
import IntegrationsPage from './pages/IntegrationsPage';
import FaleConoscoPage from './pages/FaleConoscoPage';
// import SignInPage from './pages/SignInPage';
// import SignUpPage from './pages/SignUpPage';

// Components
import { Layout } from './components/Layout';
import OfflineIndicator from './components/OfflineIndicator';

// Services
import { useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabase';
import { hasCompletedOnboarding, isOnboardingEnabled } from './lib/services/onboardingService';
import { verifyAndUpdateDatabaseSchema } from './lib/services/adminService';
import { requestNotificationPermission, checkAndUpdateFCMToken } from './lib/firebase/messaging';
import { ThemeProvider } from './contexts/ThemeContext';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (!loading && !user) {
      console.log('[PrivateRoute] No user found, redirecting to signin.');
      navigate('/signin', { state: { from: location } });
    }
  }, [user, loading, navigate, location]);
  
  if (loading) {
    return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div></div>;
  }

  return user ? <>{children}</> : null;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      console.log('[AdminRoute] No admin user found, redirecting to home.');
      navigate('/');
    }
  }, [user, isAdmin, loading, navigate]);
  
  if (loading) {
    return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div></div>;
  }

  return (user && isAdmin) ? <>{children}</> : null;
}

function App() {
  const { user, isAdmin, loading: authLoading, needsToCompleteProfile } = useAuth();

  // Estado para controlar carregamento e navegação
  const [reloading, setReloading] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  
  // Estado para o prompt de notificação
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [checkingNotificationStatus, setCheckingNotificationStatus] = useState(false);
  
  // Usar o contexto de onboarding
  const { onboardingCompleted, setOnboardingCompleted } = useOnboardingStatus();
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Verificar autenticação em rotas protegidas
  useEffect(() => {
    // Para verificar se a autenticação está funcionando corretamente
    if (import.meta.env.DEV) {
      console.log('[AUTH] Estado de autenticação:', user ? 'Autenticado' : 'Não autenticado');
      if (user) {
        console.log('[AUTH] Admin:', isAdmin ? 'Sim' : 'Não');
      }
    }
  }, [user, isAdmin]);

  // Verificar e atualizar o esquema do banco em produção (apenas administradores)
  useEffect(() => {
    const checkAndUpdateSchema = async () => {
      if (user && isAdmin && import.meta.env.PROD) {
        try {
          console.log('Verificando esquema do banco de dados...');
          const success = await verifyAndUpdateDatabaseSchema();
          
          if (success) {
            console.log('Esquema do banco de dados verificado/atualizado com sucesso');
          } else {
            console.error('Falha ao verificar/atualizar esquema do banco de dados');
          }
        } catch (error) {
          console.error('Erro ao verificar esquema do banco de dados:', error);
        }
      }
    };
    
    checkAndUpdateSchema();
  }, [user, isAdmin]);

  // Verificar onboarding para novos usuários - modificado para usar estado compartilhado
  useEffect(() => {
    const checkOnboarding = async () => {
      // Se já verificamos o status de onboarding, use o valor em cache
      if (user && isOnboardingEnabled() && !checkingOnboarding && onboardingCompleted === null) {
        try {
          setCheckingOnboarding(true);
          
          // Ignorar redirecionamento se já estamos na página de onboarding
          if (location.pathname === '/onboarding') {
            setCheckingOnboarding(false);
            return;
          }
          
          // Verificar se acabamos de navegar da página de onboarding
          const comingFromOnboarding = sessionStorage.getItem('onboarding_completed') === 'true';
          if (comingFromOnboarding) {
            console.log('[ONBOARDING] Usuário acabou de completar onboarding, atualizando estado compartilhado');
            sessionStorage.removeItem('onboarding_completed');
            setOnboardingCompleted(true);
            setCheckingOnboarding(false);
            return;
          }
          
          // Verificar se o usuário já completou o onboarding (apenas uma vez)
          try {
            const completed = await hasCompletedOnboarding(user.id);
            console.log('[ONBOARDING] Status do onboarding:', completed ? 'Completo' : 'Não completo');
            setOnboardingCompleted(completed);
            
            // Se não completou e não estamos tentando sair da aplicação, redirecionar para o onboarding
            if (!completed && 
                !location.pathname.startsWith('/signout') && 
                !location.pathname.startsWith('/onboarding')) {
              console.log('[ONBOARDING] Redirecionando novo usuário para onboarding');
              navigate('/onboarding');
            }
          } catch (error) {
            console.error('[ONBOARDING] Erro ao verificar status de onboarding:', error);
            // Em caso de erro na verificação, considerar como completo para não bloquear o usuário
            setOnboardingCompleted(true);
          }
        } catch (error) {
          console.error('[ONBOARDING] Erro ao verificar status de onboarding:', error);
          // Em caso de erro, considerar como completo para não bloquear o usuário
          setOnboardingCompleted(true);
        } finally {
          setCheckingOnboarding(false);
        }
      }
    };
    
    checkOnboarding();
  }, [user, location.pathname, navigate, checkingOnboarding, onboardingCompleted, setOnboardingCompleted]);

  // Efeito para atualizar o estado de onboarding quando o usuário muda
  useEffect(() => {
    // Resetar o estado quando o usuário muda ou faz logout
    if (!user) {
      setOnboardingCompleted(null);
    }
  }, [user, setOnboardingCompleted]);

  // Efeito para mostrar indicador de recarregamento ao pressionar F5
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Salvar a rota atual no sessionStorage ao recarregar
      sessionStorage.setItem('lastRoute', location.pathname + location.search);
      setReloading(true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location]);

  // Efeito para restaurar a rota após recarregar a página
  useEffect(() => {
    const lastRoute = sessionStorage.getItem('lastRoute');
    
    // Verificar se estamos carregando depois de um refresh e se há uma rota salva
    if (lastRoute && window.performance) {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length > 0 && (navEntries[0] as any).type === 'reload') {
        // Adicionar indicador visual de carregamento
        const indicator = document.createElement('div');
        indicator.className = 'page-reload-indicator';
        document.body.appendChild(indicator);
        
        // Remover o indicador após a navegação
        setTimeout(() => {
          document.body.classList.add('route-loading');
          // Certificar que não estamos em um loop de redirecionamento
          if (lastRoute !== location.pathname + location.search && 
              lastRoute !== '/' && 
              !lastRoute.includes('/signin')) {
            navigate(lastRoute);
          }
          
          setTimeout(() => {
            if (indicator.parentNode) {
              indicator.parentNode.removeChild(indicator);
            }
            document.body.classList.remove('route-loading');
            document.body.classList.add('route-loaded');
            
            // Limpar depois de restaurado
            sessionStorage.removeItem('lastRoute');
          }, 500);
        }, 100);
      }
    }
  }, [user, setOnboardingCompleted, location.pathname, location.search, navigate]);

  // Efeito para verificar status da notificação após login
  useEffect(() => {
    const checkNotificationStatus = async () => {
      if (user && !authLoading && !checkingNotificationStatus) {
        setCheckingNotificationStatus(true);
        console.log('[Notifications] Checking notification status...');

        try {
          // 1. Verificar se o usuário já dispensou permanentemente
          const dismissedPermanently = localStorage.getItem('hideNotificationPrompt') === 'true';
          if (dismissedPermanently) {
            console.log('[Notifications] Prompt dismissed permanently by user.');
            setShowNotificationPrompt(false);
            setCheckingNotificationStatus(false);
            return;
          }

          // 2. Verificar permissão do navegador
          if (typeof Notification === 'undefined' || !('permission' in Notification)) {
            console.log('[Notifications] Browser does not support notifications.');
            setCheckingNotificationStatus(false);
            return;
          }

          const currentPermission = Notification.permission;
          if (currentPermission === 'denied') {
            console.log('[Notifications] Permission is denied.');
            setShowNotificationPrompt(false);
            setCheckingNotificationStatus(false);
            return;
          }

          // 3. Verificar se já existe token no banco de dados
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('fcm_token')
            .eq('id', user.id)
            .single();

          if (profileError) {
            console.error('[Notifications] Error fetching profile:', profileError);
          }

          if (profile && profile.fcm_token) {
            // Verificar se o token ainda é válido tentando obter um token novo
            try {
              // Assegurar que o service worker esteja registrado
              if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
                if (!registration) {
                  console.log('[Notifications] Registrando service worker...');
                  await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                    scope: '/'
                  });
                  console.log('[Notifications] Service worker registrado com sucesso');
                }
              }
              
              // Chamar checkAndUpdateFCMToken() que já faz toda a lógica de atualizar tokens
              const token = await checkAndUpdateFCMToken();
              if (token) {
                console.log('[Notifications] FCM token verificado e/ou atualizado.');
                setShowNotificationPrompt(false);
                setCheckingNotificationStatus(false);
                return;
              }
            } catch (e) {
              console.error('[Notifications] Erro ao verificar token FCM existente:', e);
            }
          }

          // 4. Se permissão for 'granted' mas não há token, tentar registrar silenciosamente
          if (currentPermission === 'granted') {
            try {
              // Assegurar que o service worker esteja registrado
              if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
                if (!registration) {
                  console.log('[Notifications] Registrando service worker...');
                  await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                    scope: '/'
                  });
                  console.log('[Notifications] Service worker registrado com sucesso');
                }
              }
              
              console.log('[Notifications] Tentando obter token silenciosamente...');
              const token = await requestNotificationPermission();
              if (token) {
                const { error } = await supabase
                  .from('profiles')
                  .update({ fcm_token: token })
                  .eq('id', user.id);
                
                if (!error) {
                  console.log('[Notifications] FCM token registered silently');
                  setShowNotificationPrompt(false);
                  setCheckingNotificationStatus(false);
                  return;
                }
              }
            } catch (error) {
              console.error('[Notifications] Error registering token silently:', error);
            }
          }

          // 5. Se permissão for 'default' e não dispensado/negado/com token -> Mostrar prompt
          if (currentPermission === 'default') {
            console.log('[Notifications] Permission is default, showing prompt.');
            setShowNotificationPrompt(true);
          } else {
            console.log(`[Notifications] Permission is ${currentPermission}, but no token found. Not showing prompt for now.`);
            setShowNotificationPrompt(false);
          }

        } catch (error) {
          console.error('[Notifications] Error checking notification status:', error);
          setShowNotificationPrompt(false);
        } finally {
          setCheckingNotificationStatus(false);
        }
      } else if (!user && !authLoading) {
        setShowNotificationPrompt(false);
        setCheckingNotificationStatus(false);
      }
    };

    checkNotificationStatus();
  }, [user, authLoading]);

  // Função para lidar com a ativação de notificações
  const handleEnableNotifications = async () => {
    if (!user) return;

    setShowNotificationPrompt(false); // Esconde o prompt imediatamente
    const token = await requestNotificationPermission();

    if (token) {
      console.log("[Notifications] FCM Token obtained:", token);
      toast.success("Notificações ativadas!");

      try {
        const { error } = await supabase
          .from('profiles')
          .update({ fcm_token: token })
          .eq('id', user.id);

        if (error) throw error;
        console.log("[Notifications] FCM token saved to profile.");
        localStorage.setItem('hideNotificationPrompt', 'true'); // Opcional: dispensar permanentemente após ativar

      } catch (error) {
        console.error("[Notifications] Error saving FCM token:", error);
        toast.error("Erro ao salvar configuração de notificação.");
        // Considerar mostrar o prompt novamente? Ou deixar para a próxima sessão?
      }
    } else {
      // Usuário negou a permissão no prompt do NAVEGADOR ou houve erro no getToken
      toast.error("Não foi possível ativar as notificações.");
      // A permissão agora deve ser 'denied', então o prompt não deve reaparecer
      // Poderia setar o dismiss permanente aqui se Notification.permission === 'denied'
      if (Notification.permission === 'denied') {
        localStorage.setItem('hideNotificationPrompt', 'true');
      }
      console.log("[Notifications] Permission denied or token generation failed.");
    }
  };

  // Função para dispensar o prompt
  const handleDismissPrompt = (permanently: boolean) => {
    setShowNotificationPrompt(false);
    if (permanently) {
      localStorage.setItem('hideNotificationPrompt', 'true');
      console.log('[Notifications] User chose not to show prompt again.');
    } else {
       console.log('[Notifications] User dismissed prompt for this session.');
    }
  };

  // Renomeando o componente NotificationPrompt para LocalNotificationPrompt
  const LocalNotificationPrompt = ({ onEnable, onDismiss }: { onEnable: () => void; onDismiss: (permanently: boolean) => void }) => {
    return (
      <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 p-4 bg-indigo-600 text-white rounded-lg shadow-lg flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0 sm:space-x-4">
        <div className="flex items-center space-x-2">
          <Bell size={20} />
          <span>Deseja receber notificações?</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-white text-indigo-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={onEnable}
          >
            Ativar
          </button>
          <button
            className="px-3 py-1.5 text-sm font-medium rounded-md text-indigo-100 hover:bg-indigo-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={() => onDismiss(false)}
          >
            Agora não
          </button>
          <button
            className="px-2 py-1.5 text-xs font-medium rounded-md text-indigo-200 hover:text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={() => onDismiss(true)}
          >
            Não mostrar novamente
          </button>
        </div>
      </div>
    );
  };

  // Adicionar um efeito para logar needsToCompleteProfile quando mudar
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[APP_NEEDS_PROFILE_COMPLETE_STATUS]', needsToCompleteProfile);
    }
  }, [needsToCompleteProfile]);

  // Renderização condicional baseada no estado de autenticação e conclusão de perfil
  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="ml-4 text-lg font-medium text-gray-700">Carregando Aplicação...</p>
      </div>
    );
  }

  // Se o usuário está autenticado mas precisa completar o perfil, mostrar a página de completar perfil
  if (user && needsToCompleteProfile) {
    return (
      <ThemeProvider>
        <Routes>
          <Route path="*" element={<CompleteProfilePage />} />
        </Routes>
      </ThemeProvider>
    );
  }
  
  // Renderização principal do App
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gray-50">
        <Toaster position="top-right" />
        <OfflineIndicator />
        {showNotificationPrompt && (
          <LocalNotificationPrompt 
            onEnable={handleEnableNotifications} 
            onDismiss={handleDismissPrompt} 
          />
        )}
        <Layout>
          <Routes>
            {/* Rotas Públicas */}
            <Route path="/" element={<HomePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/signin" element={<AuthForm mode="signin" />} />
            <Route path="/signup" element={<AuthForm mode="signup" />} />
            <Route path="/reset-password" element={<AuthForm mode="signin" />} />
            <Route path="/games/titulo/:gameSlug" element={<GameDetailsPage />} />
            <Route path="/games/:gameSlug" element={<GameDetailsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/ranking" element={<RankingPage />} />
            <Route path="/profile/:username" element={<PublicProfilePage />} />
            <Route path="/users/search" element={<UserSearchPage />} />
            <Route path="/market" element={<MarketPage />} />
            <Route path="/market/:listingId" element={<MarketListingDetails />} />
            <Route path="/ludopedia/callback" element={<LudopediaCallback />} />
            <Route path="/games/titulo/:gameSlug/avaliacoes" element={<GameReviewsPage />} />
            <Route path="/games/:gameId/avaliacoes" element={<GameReviewsPage />} />
            <Route path="/post/:postId" element={<PostPage />} />
            <Route path="/delete-account-request" element={<DeleteAccountRequestPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/stores" element={<StoresPage />} />
            <Route path="/stores/:storeId/products" element={<StoreProductsPage />} />
            <Route path="/game-prices/:gameId" element={<GamePricesPage />} />
            <Route path="/game-prices/slug/:gameSlug" element={<GamePricesPage />} />
            <Route path="/fale-conosco" element={<FaleConoscoPage />} />

            {/* Rotas de Integração - NOVAS ROTAS ABAIXO */}
            <Route path="/integrations" element={<PrivateRoute><IntegrationsPage /></PrivateRoute>} />
            <Route path="/auth/ludopedia/callback" element={<PrivateRoute><LudopediaCallback /></PrivateRoute>} />
            {/* FIM DAS NOVAS ROTAS DE INTEGRAÇÃO */}

            {/* Rotas Privadas (Requerem Login) */}
            <Route path="/feed" element={<PrivateRoute><FeedPage /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><ProfileForm /></PrivateRoute>} />
            <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
            <Route path="/add-game" element={<PrivateRoute><AddGamePage /></PrivateRoute>} />
            <Route path="/collection" element={<PrivateRoute><CollectionPage /></PrivateRoute>} />
            <Route path="/collection/stats" element={<PrivateRoute><CollectionStatsPage /></PrivateRoute>} />
            <Route path="/collection/played" element={<PrivateRoute><PlayedGamesPage /></PrivateRoute>} />
            <Route path="/sessions/:sessionId" element={<PrivateRoute><SessionDetailsPage /></PrivateRoute>} />
            <Route path="/wishlist" element={<PrivateRoute><WishlistPage /></PrivateRoute>} />
            <Route path="/recommendations" element={<PrivateRoute><RecommendationsPage /></PrivateRoute>} />
            <Route path="/onboarding" element={<PrivateRoute><OnboardingPage /></PrivateRoute>} />
            <Route path="/feedback" element={<PrivateRoute><FeedbackPage /></PrivateRoute>} />
            <Route path="/connections" element={<PrivateRoute><UserConnectionsPage /></PrivateRoute>} />
            <Route path="/messages" element={<PrivateRoute><MessagesPage /></PrivateRoute>} />
            <Route path="/messages/:username" element={<PrivateRoute><ConversationPage /></PrivateRoute>} />
            <Route path="/excluir-conta" element={<PrivateRoute><AccountDeletionPage /></PrivateRoute>} />
            <Route path="/profile/:usernameOrId" element={<PrivateRoute><UserProfilePage /></PrivateRoute>} />
            <Route path="/profile/xp-history" element={<PrivateRoute><XpHistoryPage /></PrivateRoute>} />
            <Route path="/profile/choose-mascot" element={<PrivateRoute><ChooseMascotPage /></PrivateRoute>} />
            <Route path="/my-badges" element={<PrivateRoute><MyBadgesPage /></PrivateRoute>} />
            <Route path="/profile/missions" element={<PrivateRoute><UserMissionsPage /></PrivateRoute>} />

            {/* Rotas de Admin (Requerem Login e ser Admin) */}
            <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/edit-game/:gameId" element={<AdminRoute><EditGamePage /></AdminRoute>} />

            {/* Rota de Erro */}
            <Route path="/error" element={<ErrorPage />} />

            {/* Rota Not Found (404) */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
      </div>
    </ThemeProvider>
  );
}

export default App;